import { useState, useCallback } from "react";

type UseQueryParamOpts<T> = {
	initialValue?: T;
	valueFromString?: (val?: string) => T;
	valueToString?: (val?: T) => string;
	onChange?: (prev?: string, next?: string) => any;
};

/**
 * Hook that will read and persist state in query search param.
 *
 * Useful when i.e. navigating between tabs and want to save state
 * in history so browsers back button can be used.
 */
export function useQueryState<T = string>(
	key: string,
	{
		initialValue,
		valueFromString: valueFromString,
		valueToString,
		onChange,
	}: UseQueryParamOpts<T> = {}
): [T, (val?: T) => any] {
	const rawQueryStringValue = getQueryStringValue(key);

	const stringifiedInitialValue = typeof initialValue === "undefined" ? "" : initialValue + "";

	const [value, setValue] = useState<string | undefined>(
		rawQueryStringValue || (valueToString ? valueToString(initialValue) : stringifiedInitialValue)
	);

	const onSetValue = useCallback(
		(newValue?: T) => {
			const parsedValue =
				(valueToString ? valueToString(newValue) : newValue ? newValue + "" : "") || "";
			setValue((prev) => {
				onChange && prev !== parsedValue && onChange(prev, parsedValue);
				return parsedValue;
			});
			setQueryStringValue(key, parsedValue);
		},
		[key]
	);

	// @ts-ignore: Todo - I got tired fighting TS /JS
	return [valueFromString ? valueFromString(value) : (value as T), onSetValue];
}

export default useQueryState;

export function getQueryStringValue(key: string) {
	const params = new URLSearchParams(document ? document.location.search : undefined);
	const val = params.get(key);
	return val || undefined;
}

export function setQueryStringValue(key: string, value: string | undefined) {
	const params = new URLSearchParams(document ? document.location.search : undefined);

	// If value is undefined, delete the query param
	if (value === undefined) {
		params.delete(key);
	} else {
		params.set(key, value);
	}

	const queryString = params.toString();
	let newurl =
		window.location.protocol +
		"//" +
		window.location.host +
		window.location.pathname +
		(queryString ? "?" + queryString : "");
	window.history.pushState({ path: newurl }, "", newurl);
}
